import React, { useEffect, useState } from "react";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ThreeCircles } from "react-loader-spinner";
import CustomButton from "../../reusable_components/custom_button_component/Button";
import ClientApproverSetting from "../../../Reusables/components/ClientApproverSetting/ClientApproverSetting";
import { Approver } from "../../../Reusables/components/ClientApproverSetting/ClientApproverSetting.utils";
import styles from "./ApprovalSettingModal.module.css";
import * as Icon from "react-bootstrap-icons";
import { areApproversEqual } from "./ApprovalSettingModal.utils";
import {
  fetchAllUsers,
  fetchExistingApprovers,
  saveApproverSettings,
} from "./ApprovalSettingModal.service";

interface ApprovalSettingsModalProps {
  templateId: number | null;
  clientMasterId: number;
  processedTemplateId: number | null;
  title: string;
  showSkipApprovalCheck: boolean;
  show: boolean;
  onHide: () => void;
}

const ApprovalSettingsModal: React.FC<ApprovalSettingsModalProps> = ({
  templateId,
  clientMasterId,
  processedTemplateId,
  title,
  showSkipApprovalCheck = true,
  show,
  onHide,
}) => {
  const [allUsers, setAllUsers] = useState<Approver[]>([]);
  const [initialApprovers, setInitialApprovers] = useState<Approver[]>([]);
  const [selectedApprovers, setSelectedApprovers] = useState<Approver[]>([]);
  const [loading, setLoading] = useState(false);
  const [apiProgress, setApiProgress] = useState(false);
  const [approvalCheck, setApprovalCheck] = useState<boolean>(false);
  const [approvalRadio, setApprovalRadio] = useState<"Parallel" | "Sequential">("Parallel");

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApprovalRadio(event.target.value as "Parallel" | "Sequential");
  };

  const handleApprovalCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApprovalCheck(event.target.checked);
  };

  useEffect(() => {
    fetchAllUsers(setAllUsers);
  }, []);

  useEffect(() => {
    fetchExistingApprovers(
      templateId, 
      processedTemplateId, 
      setInitialApprovers, 
      setSelectedApprovers, 
      setApprovalCheck, 
      setApprovalRadio,
      setLoading
    );
  }, [processedTemplateId, templateId]);

  const handleReapplyRules = () => {
    setLoading(true);
    fetchExistingApprovers(
      templateId, 
      processedTemplateId, 
      setInitialApprovers, 
      setSelectedApprovers, 
      setApprovalCheck, 
      setApprovalRadio,
      setLoading,
      clientMasterId,
      true
    );
  };

  const isDisabled = !!processedTemplateId && !!templateId || !showSkipApprovalCheck;
  
  const hasChanges =
  !areApproversEqual(initialApprovers, selectedApprovers) ||
  apiProgress ||
  approvalRadio !== (initialApprovers.length > 0 && initialApprovers[0].ApprovalType) ||
  approvalCheck !== (initialApprovers.length > 0 && initialApprovers[0].ApprovalRequired === "N");
  
  return (
    <Modal
      show={show}
      onHide={onHide}
      className={styles.approvalSettingModal}
      centered
      enforceFocus={false}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className={styles.approvalSettingModalHeader} closeButton>
        <div className={styles.clientNameWrapper}>
          <b>{title}</b>
        </div>
      </Modal.Header>
      <Modal.Body className={styles.approvalSettingModalBody}>
        {loading ? (
          <div className="flex-column-center-center">
            <ThreeCircles
              height="50"
              width="50"
              color="#4fa94d"
              wrapperStyle={{}}
              wrapperClass=""
              visible={true}
              ariaLabel="three-circles-rotating"
              outerCircleColor="var(--theme1_tertiary)"
              innerCircleColor="var(--theme1_secondary)"
              middleCircleColor="var(--theme1_primary)"
            />
            <span>Loading your approval setting</span>
          </div>
        ) : (
          <div className={styles.approvalSettingWrapper}>
            {!isDisabled && (
              <div className={styles.skipApprovalCheck}>
                <input
                  type="checkbox"
                  className={`form-check-input move-left custom-check-input pivot-check ${styles.skipCheck}`}
                  checked={approvalCheck}
                  onChange={handleApprovalCheck}
                  autoComplete="new-password"
                />
                <span className={styles.skipCheckLabel}>
                  Please select this if you want to skip approval process.
                </span>
              </div>
            )}
            <div className={styles.preferenceHeaderRow}>
              <div className={styles.preferenceLeftSection}>
                <span className={styles.preferenceTitle}>Approval Preference</span>
                <OverlayTrigger
                  placement="right"
                  delay={{ show: 250, hide: 400 }}
                  overlay={
                    <Tooltip id="button-tooltip">
                      Approval preference determines how client emails are sent:
                      Use "Sequential" to notify approvers one after another,
                      or "Parallel" to notify all at once.
                    </Tooltip>
                  }
                >
                  <Icon.PatchQuestion className={styles.questionIcon} />
                </OverlayTrigger>
              </div>
              <button
                className={styles.reapplyButton}
                onClick={handleReapplyRules}
                disabled={loading || approvalCheck}
              >
                <Icon.ArrowClockwise size={16} />
                <span className={styles.reapplyText}>Re-Apply Rules</span>
              </button>
            </div>
            <div className={styles.preferenceRadios}>
            <div>
                <input
                  className={`form-check-input ${styles.preferenceRadio} ${approvalCheck ? styles.disabled : ''}`}
                  type="radio"
                  name="approvalPreference"
                  id="send_for_approval_sequential"
                  value="Sequential"
                  checked={approvalRadio === "Sequential"}
                  onChange={handleRadioChange}
                  disabled={approvalCheck}
                />
                <label
                  className={styles.preferenceLabel}
                  htmlFor="send_for_approval_sequential"
                >
                  Sequential
                </label>
              </div>
              <div>
                <input
                  className={`form-check-input ${styles.preferenceRadio} ${approvalCheck ? styles.disabled : ''}`}
                  type="radio"
                  name="approvalPreference"
                  id="send_for_approval_parallel"
                  value="Parallel"
                  checked={approvalRadio === "Parallel"}
                  onChange={handleRadioChange}
                  disabled={approvalCheck}
                />
                <label
                  className={styles.preferenceLabel}
                  htmlFor="send_for_approval_parallel"
                >
                  Parallel
                </label>
              </div>
            </div>
            <div className={styles.approverCardContainer}>
              <ClientApproverSetting
                allUsers={allUsers}
                selectedApprovers={selectedApprovers}
                approvalCheck={approvalCheck}
                setSelectedApprovers={setSelectedApprovers}
              />
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <CustomButton onclick={onHide} content="Close" />
        <CustomButton
          progress={apiProgress}
          onclick={() => saveApproverSettings(
            templateId,
            processedTemplateId,
            approvalCheck,
            selectedApprovers,
            approvalRadio,
            setApiProgress,
            onHide
          )}
          content="Save"
          disabled={!hasChanges}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default ApprovalSettingsModal;
